import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import { UserRecommendationsGridLayout } from './UserRecommendationsGridLayout.tsx';
import { type UserRecommendationsTitleAlign } from './UserRecommendationsTitle.tsx';
import { UserRecommendationsRecommendationTile } from './UserRecommendationsRecommendationTile.tsx';
import { useUserRecommendationsGridExpand } from './useUserRecommendationsGridExpand.ts';
import { useUserRecommendationsItemCounts } from './useUserRecommendationsItemCounts.ts';
import { getUserRecommendationsItemsPerRow } from './userRecommendationsGridConstants.ts';

import { type UserRecommendationsGrid_user$key } from './__generated__/UserRecommendationsGrid_user.graphql.ts';
import { type UserRecommendationsGrid_viewer$key } from './__generated__/UserRecommendationsGrid_viewer.graphql.ts';

const userFragment = graphql`
    fragment UserRecommendationsGrid_user on User
    @argumentDefinitions(
        recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
        recommendationItemsCount: { type: "Int", defaultValue: 20 }
    ) {
        ...UserRecommendationsRecommendationTile_user
        twoTowerModelItemRecommendations(type: $recommendationType) {
            items(first: $recommendationItemsCount) {
                serviceId
                ...UserRecommendationsRecommendationTile_item
                    @arguments(
                        fetchFavorites: true
                        userId: $userId
                        isTrade: $isTrade
                        priceBookName: $priceBookName
                    )
            }
        }
    }
`;

const viewerFragment = graphql`
    fragment UserRecommendationsGrid_viewer on Viewer {
        ...UserRecommendationsRecommendationTile_viewer
            @arguments(fetchFavorites: true, userId: $userId)
    }
`;

export type UserRecommendationsGridProps = {
    user: UserRecommendationsGrid_user$key | null | undefined;
    viewer: UserRecommendationsGrid_viewer$key;
    trackingTrigger: string;
    isMobile: boolean;
    isTablet: boolean;
    dataTn?: string;
    title?: ReactNode;
    currency: string;
    titleAlign?: UserRecommendationsTitleAlign;
    autoLoadMore?: boolean;
    afterDisplay?: boolean;
};

export const UserRecommendationsGrid: FC<UserRecommendationsGridProps> = ({
    user: userRef,
    viewer: viewerRef,
    trackingTrigger,
    isMobile,
    isTablet,
    dataTn = 'user-recommendations',
    title,
    currency,
    titleAlign = 'center',
    autoLoadMore = false,
    afterDisplay = false,
}) => {
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { initialItemCount, maxItemCount } = useUserRecommendationsItemCounts(isMobile);
    const items = useMemo(
        () =>
            (user?.twoTowerModelItemRecommendations?.items || [])
                .filter(filterFalsy)
                .slice(0, maxItemCount),
        [maxItemCount, user?.twoTowerModelItemRecommendations?.items]
    );

    const hasMoreToLoad = items.length > initialItemCount;
    const handleFragmentLoadMore = useCallback(() => {}, []);

    const {
        isExpanded,
        moduleRef,
        showViewLess,
        showToggleButton,
        handleLoadMore,
        handleToggleExpand,
    } = useUserRecommendationsGridExpand({
        trackingTrigger,
        autoLoadMore,
        hasMoreToLoad,
        onLoadMore: handleFragmentLoadMore,
    });

    const visibleItems = isExpanded ? items : items.slice(0, initialItemCount);
    const itemsPerRow = getUserRecommendationsItemsPerRow(isMobile, isTablet);

    if (!user || !items.length) {
        return null;
    }

    return (
        <UserRecommendationsGridLayout
            isMobile={isMobile}
            isTablet={isTablet}
            dataTn={dataTn}
            title={title}
            titleAlign={titleAlign}
            moduleRef={moduleRef}
            showToggleButton={showToggleButton}
            showViewLess={showViewLess}
            autoLoadMore={autoLoadMore}
            hasMoreToLoad={hasMoreToLoad}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            onLoadMore={handleLoadMore}
        >
            {visibleItems.map((item, index) => (
                <UserRecommendationsRecommendationTile
                    key={item.serviceId || index}
                    item={item}
                    viewer={viewer}
                    user={user}
                    index={index}
                    itemsPerRow={itemsPerRow}
                    isMobile={isMobile}
                    trackingTrigger={trackingTrigger}
                    dataTn={dataTn}
                    currency={currency}
                    afterDisplay={afterDisplay}
                />
            ))}
        </UserRecommendationsGridLayout>
    );
};
